import { RoleData, UserDataWithRoles, UserOrganizerData, UserSourceType } from '../../@types/user';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import {
  EventData,
  EventDataWithSoldTickeQty,
  EventDataWithDetailedStatistics,
  TicketSalesStatisticsResponse,
} from 'src/@types/event';
import { PATH_AUTH } from 'src/routes/paths';
import { PaymentStatus, SettlementListResponse } from 'src/@types/settlement';
import { socket } from 'src/services/socket/socket.io';
import { handleRefreshToken } from 'src/utils/jwt';
import { userRolesActions } from '../user-roles';
import { getPermissionsByRoles } from 'src/utils/permissions-by-roles';
import axios from 'src/utils/axios';
import { appEnvVariables, featureFlags } from 'src/config';
import { ApiType, Environment, FeatureFlags, PaginationMetadata } from 'src/@types/types';
import {
  Coupon,
  EventCouponStatistics,
  CouponCodeResponse,
  CouponStatisticsGraphResponse,
} from 'src/@types/coupon';
import { CustomizedStatistics } from 'src/@types/customized-statistics';
import {
  EventGuestsSearchType,
  EventWithGuestData,
  GuestHistory,
  GuestHistoryAction,
  GuestInvitationWithRelatedData,
} from 'src/@types/guests';
import {
  BoxOfficeOrderData,
  BoxOfficeOrderHistory,
  DailyClosingResponse,
} from 'src/@types/box-office';
import { ExportResponse } from 'src/@types/export';

type UserQueryProps = {
  organizerId?: number;
  skip: number;
  rowsPerPage: number;
  orderBy: string;
  order: string;
  filterRole?: string;
  searchQuery: string;
  roles?: boolean;
  sourceType?: UserSourceType;
};

type EventQueryProps = {
  organizerId?: number;
  roleId?: number;
  skip: number;
  rowsPerPage: number;
  orderBy: string;
  order: string;
  filterStatus: string;
  searchQuery: string;
  url?: boolean;
  ticketTypes?: boolean;
  offlineTicketTypes?: boolean;
  boxOffice?: boolean;
};

type EventStatisticsQueryProps = {
  eventId?: number;
  organizerId?: number;
  roleId?: number;
  timePeriod: string;
  filterFromDate?: string;
  filterToDate?: string;
  includeChartData?: boolean;
};

type EventWithGuestDataQueryProps = {
  userId?: number;
  organizerId?: number;
  skip: number;
  rowsPerPage: number;
  orderBy: string;
  order: string;
  filterStatus: string;
  ticketTypes: boolean;
  searchQuery: string;
  searchType: EventGuestsSearchType;
};

type SettlementQueryProps = {
  organizerId?: number;
  orderBy: string;
  order: string;
  paymentStatus?: PaymentStatus;
  filterFromDate?: string;
  filterToDate?: string;
  events?: number[];
};

type CouponsQueryProps = {
  organizerId?: number;
  skip?: number;
  rowsPerPage?: number;
  orderBy?: string;
  order?: string;
  filterStatus?: string;
  searchText?: string;
  all?: boolean;
};

type CouponStatisticsQueryProps = {
  organizerId?: number;
  couponId?: number;
  skip: number;
  rowsPerPage: number;
  orderBy: string;
  order: string;
  filterFromDate?: string;
  filterToDate?: string;
};

type CouponStatisticsGraphQueryProps = {
  organizerId?: number;
  couponId: number;
  filterFromDate?: string;
  filterToDate?: string;
};

type CouponCodesQueryProps = {
  organizerId?: number;
  couponId: number;
  skip: number;
  rowsPerPage: number;
  isActive: boolean;
  searchText: string;
};

type CustomizedStatisticsQueryProps = {
  organizerId?: number;
  skip: number;
  rowsPerPage: number;
  orderBy: string;
  order: string;
  searchText: string;
};

type GuestlistQueryProps = {
  organizerId?: number;
  roleId?: number;
  skip: number;
  rowsPerPage: number;
  searchText: string;
  userIds?: number[];
  eventIds?: number[];
};

type GuestHistoryQueryProps = {
  organizerId?: number;
  eventId: number;
  guestInvitationId: number;
  skip: number;
  rowsPerPage: number;
  action?: GuestHistoryAction;
  searchText: string;
};

type BoxOfficeOrderQueryProps = {
  organizerId?: number;
  skip: number;
  rowsPerPage: number;
  roleId?: number;
  searchText?: string;
  userIds?: number[];
  eventIds?: number[];
};

type BoxOfficeOrderHistoryQueryProps = {
  organizerId?: number;
  eventId: number;
  orderId: number;
  skip: number;
  rowsPerPage: number;
  searchText?: string;
  action?: string;
};

type BoxOfficeDailyClosingQueryProps = {
  organizerId?: number;
  skip: number;
  rowsPerPage: number;
  filterFromDate?: string;
  filterToDate?: string;
  userIds?: number[];
  eventIds?: number[];
};

type ExportJobsQueryProps = {
  organizerId?: number;
  limit: number;
  offset: number;
};

type ListEventsStatisticsQueryProps = {
  organizerId?: number;
  rowsPerPage: number;
  skip: number;
  orderBy: string;
  order: string;
  filterStatus: string;
  timePeriod: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
};

const paramsSerializer = (params: Record<string, any>) => {
  const queryParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];

    if (Array.isArray(value)) {
      value.forEach((item) => {
        queryParams.append(`${key}[]`, item);
      });
    } else {
      typeof value !== 'undefined' && queryParams.append(key, value);
    }
  }

  return queryParams.toString();
};

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions: { apiType?: ApiType }
) => {
  const baseUrl =
    extraOptions?.apiType === ApiType.NEW ? appEnvVariables.newApi : appEnvVariables.api;

  const query = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('accessToken');

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    },
    paramsSerializer,
  });

  return query(args, api, extraOptions);
};

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await dynamicBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      await handleRefreshToken({ refreshToken });

      result = await dynamicBaseQuery(args, api, extraOptions);
    } else {
      window.location.href = PATH_AUTH.login;
    }
  }

  return result;
};

export const userApi = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'User',
    'Event',
    'EventWithTicketType',
    'EventWithStatistics',
    'EventCouponStatistics',
    'SettlementList',
    'CouponCodeList',
    'Guestlist',
    'GuestHistory',
    'ExportList',
  ],
  endpoints: (build) => ({
    getFeatureFlags: build.query<
      { [key: string]: { enabled: boolean } | undefined },
      { organizerId?: number }
    >({
      queryFn: async ({ organizerId }) => {
        if (appEnvVariables.environment === Environment.LOCAL) {
          return { data: featureFlags.localOverrides };
        }

        try {
          const { data } = await axios.get<FeatureFlags>(
            `${featureFlags.apiHost}/configurations/feature-toggles`,
            {
              headers: {
                'X-API-Key': featureFlags.apiKey || '',
              },
              params: {
                organizerId,
              },
            }
          );

          return { data };
        } catch (error) {
          throw new Error(error);
        }
      },
    }),
    getOrganizers: build.query<UserOrganizerData[], any>({
      query: (userId) => `user/${userId}/organizer`,
      async onCacheEntryAdded(arg, { updateCachedData, cacheEntryRemoved }) {
        try {
          socket.on('onUserOrganizers', (data) => {
            updateCachedData(() => data);
          });

          await cacheEntryRemoved;

          socket.off('connect', () => {
            console.log('Disconnect');
          });
          socket.off('onUserOrganizers', () => {
            console.log('Disconnect organizers');
          });
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getRoles: build.query<RoleData[], any>({
      query: ({ userId, organizerId }) => ({
        url: `user/${userId}/organizer/${organizerId}/role`,
      }),

      async onCacheEntryAdded(arg, { dispatch, updateCachedData, cacheEntryRemoved }) {
        try {
          socket.on('onUserRoles', (data: RoleData[]) => {
            const { setPermissionsByRoles } = userRolesActions;

            const permissionsByRoles = getPermissionsByRoles(data);

            dispatch(setPermissionsByRoles(permissionsByRoles));
            dispatch(
              userApi.util.invalidateTags([
                'Event',
                'EventWithTicketType',
                'EventWithStatistics',
                'SettlementList',
              ])
            );

            updateCachedData(() => data);
          });

          await cacheEntryRemoved;

          socket.off('connect', () => {
            console.log('Disconnect');
          });
          socket.off('onUserRoles', () => {
            console.log('Disconnect user roles');
          });
        } catch (error) {
          console.error(error);
        }
      },
    }),
    getUsersByOrganizer: build.query<{ data: UserDataWithRoles[]; total: number }, UserQueryProps>({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/user`,
        params: queries,
      }),
      providesTags: ['User'],
    }),
    getEventsByOrganizer: build.query<{ data: EventData[]; total: number }, EventQueryProps>({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event`,
        params: queries,
      }),
      providesTags: ['Event'],
    }),
    getEventsWithTicketTypesByOrganizer: build.query<
      { data: EventDataWithSoldTickeQty[]; total: number },
      Omit<EventQueryProps, 'filterStatus' | 'offlineTicketTypes'>
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/limit-management`,
        params: queries,
      }),
      providesTags: ['EventWithTicketType'],
    }),
    getBoxOfficeOrders: build.query<
      { data: BoxOfficeOrderData[]; total: number },
      BoxOfficeOrderQueryProps
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event/box-office/orders`,
        params: queries,
      }),
    }),
    getBoxOfficeOrderHistory: build.query<
      { data: BoxOfficeOrderHistory[]; total: number },
      BoxOfficeOrderHistoryQueryProps
    >({
      query: ({ organizerId, eventId, orderId, ...queries }) => ({
        url: `organizer/${organizerId}/event/${eventId}/box-office/order/${orderId}/history`,
        params: queries,
      }),
    }),
    getBoxOfficeDailyClosing: build.query<DailyClosingResponse, BoxOfficeDailyClosingQueryProps>({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event/box-office/daily-closing`,
        params: queries,
      }),
    }),
    getEventWithStatistics: build.query<EventDataWithDetailedStatistics, EventStatisticsQueryProps>(
      {
        query: ({ eventId, organizerId, ...queries }) => ({
          url: `organizer/${organizerId}/event/${eventId}/statistics`,
          params: queries,
        }),
        providesTags: ['EventWithStatistics'],
      }
    ),
    getEventsWithStatisticsByOrganizer: build.query<
      { data: TicketSalesStatisticsResponse[]; total: number },
      ListEventsStatisticsQueryProps
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event/statistics`,
        params: queries,
      }),
      providesTags: ['EventWithStatistics'],
    }),
    getEventCouponStatistics: build.query<
      { data: EventCouponStatistics[]; total: number },
      CouponStatisticsQueryProps
    >({
      query: ({ organizerId, couponId, ...queries }) => ({
        url: `organizer/${organizerId}/coupon/${couponId}/coupon-statistics/events`,
        params: queries,
      }),
      providesTags: ['EventCouponStatistics'],
    }),
    getEventsWithGuestData: build.query<
      { data: EventWithGuestData[]; total: number },
      EventWithGuestDataQueryProps
    >({
      query: ({ userId, organizerId, ...queries }) => ({
        url: `user/${userId}/organizer/${organizerId}/event-guests`,
        params: queries,
      }),
    }),
    getCouponStatisticsGraph: build.query<
      CouponStatisticsGraphResponse,
      CouponStatisticsGraphQueryProps
    >({
      query: ({ organizerId, couponId, ...queries }) => ({
        url: `organizer/${organizerId}/coupon/${couponId}/coupon-statistics`,
        params: queries,
      }),
    }),
    getSettlementList: build.query<SettlementListResponse, SettlementQueryProps>({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event/settlement`,
        params: queries,
      }),
      providesTags: ['SettlementList'],
    }),
    getCouponsList: build.query<{ data: Coupon[]; total: number }, CouponsQueryProps>({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/coupon`,
        params: queries,
      }),
    }),
    getCouponCodesList: build.query<
      { data: CouponCodeResponse[]; total: number },
      CouponCodesQueryProps
    >({
      query: ({ organizerId, couponId, ...queries }) => ({
        url: `organizer/${organizerId}/coupon/${couponId}/code`,
        params: queries,
      }),
      providesTags: ['CouponCodeList'],
    }),
    getCustomizedStatisticsList: build.query<
      { data: CustomizedStatistics[]; total: number },
      CustomizedStatisticsQueryProps
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/customized-statistics`,
        params: queries,
      }),
    }),
    getGuestlist: build.query<
      { data: GuestInvitationWithRelatedData[]; total: number },
      GuestlistQueryProps
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizer/${organizerId}/event/guest-invitation`,
        params: queries,
      }),
      providesTags: ['Guestlist'],
    }),
    getGuestHistory: build.query<{ data: GuestHistory[]; total: number }, GuestHistoryQueryProps>({
      query: ({ organizerId, eventId, guestInvitationId, ...queries }) => ({
        url: `organizer/${organizerId}/event/${eventId}/guest-invitation/${guestInvitationId}/history`,
        params: queries,
      }),
      providesTags: ['GuestHistory'],
    }),
    getExportJobs: build.query<
      { data: ExportResponse[]; meta: PaginationMetadata },
      ExportJobsQueryProps
    >({
      query: ({ organizerId, ...queries }) => ({
        url: `organizers/${organizerId}/jobs/exports`,
        params: queries,
      }),
      providesTags: ['ExportList'],
      extraOptions: { apiType: ApiType.NEW },
    }),
  }),
});

export const {
  usePrefetch,
  useGetOrganizersQuery,
  useGetRolesQuery,
  useGetUsersByOrganizerQuery,
  useGetEventsByOrganizerQuery,
  useGetEventsWithTicketTypesByOrganizerQuery,
  useGetBoxOfficeOrdersQuery,
  useGetBoxOfficeOrderHistoryQuery,
  useGetBoxOfficeDailyClosingQuery,
  useGetEventWithStatisticsQuery,
  useGetEventsWithStatisticsByOrganizerQuery,
  useGetEventCouponStatisticsQuery,
  useGetEventsWithGuestDataQuery,
  useGetSettlementListQuery,
  useGetFeatureFlagsQuery,
  useGetCouponsListQuery,
  useLazyGetCouponsListQuery,
  useGetCouponCodesListQuery,
  useGetCustomizedStatisticsListQuery,
  useGetCouponStatisticsGraphQuery,
  useGetGuestlistQuery,
  useGetGuestHistoryQuery,
  useGetExportJobsQuery,
} = userApi;
